import { HashRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { MessageCircleOff } from 'lucide-react';
import i18n from './i18n';
import { fetchMasterLocale, hydrateMasterLocaleFromCache, getLanguages } from './redux/translationSlice';
import { fetchFeatureFlags } from './redux/featureSlice';
import AppLayout from './components/layout/AppLayout';
import Login from './pages/Login';
import PrivateRoute from './components/PrivateRoute.jsx';
import Appointment from './pages/Appointment';
import AppointmentList from './pages/AppointmentList';
import Doctor from './pages/Doctor';
import DoctorProfile from './pages/profile/DoctorProfile';
import MyDoctor from './pages/MyDoctor';
import Patient from './pages/Patient';
import MyPatient from './pages/MyPatient';
import SharedVisits from './pages/SharedVisits.jsx';
import Payment from './pages/Payments.jsx';
import PatientProfile from './pages/profile/PatientProfile';
import NotFound from './components/NotFound.jsx';
import NetworkErrorPage from './components/NetworkErrorPage.jsx';
import ServerErrorPage from './components/ServerErrorPage.jsx';
// import RestrictedAccess from './components/RestrictedAccess.jsx';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PatientAppointment from './pages/PatientAppointment.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';
import ActivateAccount from './pages/ActivateAccount.jsx';
import useDoctorRegistrationOpen from './hooks/useDoctorRegistrationOpen';
import './i18n';
import MyAppointment from './pages/MyAppointment.jsx';
import AdminProfile from './pages/profile/AdminProfile.jsx';
import DoctorDashboard from './pages/Dashboard/DoctorDashboard.jsx';
import PatientDashboard from './pages/Dashboard/PatientDashboard.jsx';
import ReportsDoctors from './pages/reports/ReportsDoctors.jsx';
import ReportsPatients from './pages/reports/ReportsPatients.jsx';
import ReportsCount from './pages/reports/ReportsCount.jsx';
import ReportsDoctorStatus from './pages/reports/ReportsDoctorStatus.jsx';
import AdminDashboard from './pages/Dashboard/AdminDashboard.jsx';
import { ErrorProvider, useError, ERROR_TYPES } from './contexts/ErrorContext.jsx';
import { setGlobalErrorHandler } from './hooks/useAxiosAuth.js';
import { SupportSocketProvider } from './contexts/SupportSocketContext.jsx';
import { isSessionMismatch, forceLogout } from './utils/sessionGuard.js';
// import ChatPage from './pages/ChatPage.jsx';
import PatientChatPage from './pages/chat/PatientChatPage.jsx';
import DoctorChatPage from './pages/chat/DoctorChatPage.jsx';
import TeamChatPage from './pages/chat/TeamChatPage.jsx';
import ServiceBilling from './pages/billing/ServiceBilling.jsx';
import PatientBilling from './pages/billing/PatientBilling.jsx';
import ServiceReports from './pages/reports/ServiceReports.jsx';
import CareVisits from './pages/care/CareVisits.jsx';
import Support from './pages/support/Support.jsx';
import SupportChatPage from './pages/support/SupportChatPage.jsx';
import Home from './pages/homePage/Home.jsx';
import DoctorRegister from './pages/homePage/DoctorRegister.jsx';
import { PATIENT_WEB_URL, patientRegisterUrl } from './utils/patientWebUrl';
import RegisterChoice from './pages/homePage/RegisterChoice.jsx';
// The four PUBLIC static pages. They share PublicNav/PublicFooter with Home
// (src/components/public/), so the chrome on a legal page cannot drift away
// from the chrome on the home page.
import About from './pages/public/About.jsx';
import Terms from './pages/public/Terms.jsx';
import Privacy from './pages/public/Privacy.jsx';
import Contact from './pages/public/Contact.jsx';
import ClickToGo from './pages/ClickToGo.jsx';
import Subscription from './pages/Subscription.jsx';
import SubItems from './pages/SubItems.jsx';
import MySubscription from './pages/MySubscription.jsx';
import SubscriptionPayments from './pages/SubscriptionPayments.jsx';
import FeatureGuard from './components/FeatureGuard.jsx';
import ReadOnlyGuard from './components/family/ReadOnlyGuard.jsx';
import RoleRoute from './components/RoleRoute.jsx';
import AIReports from './pages/AIReports.jsx';
import SuperAdminMoved from './pages/SuperAdminMoved.jsx';
import Settings from './pages/Settings.jsx';
import Medicines from './pages/Medicines.jsx';
import DoctorSettingsPage from './pages/DoctorSettingsPage.jsx';
import Users from './pages/Users.jsx';
import UserRoles from './pages/UserRoles.jsx';
import StaffDashboard from './pages/Dashboard/StaffDashboard.jsx';
import StaffProfile from './pages/StaffProfile.jsx';
import TenantFeatureRoute from './components/TenantFeatureRoute.jsx';
import HospitalCheckSets from './pages/HospitalCheckSets.jsx';
import HospitalProgrammes from './pages/HospitalProgrammes.jsx';
import OpenPlatformRoute from './components/OpenPlatformRoute.jsx';
import DoctorReview from './pages/DoctorReview.jsx';
import PermissionGuard from './components/PermissionGuard.jsx';
import PharmacyCounter from './pages/pharmacy/PharmacyCounter.jsx';
import MedicineMaster from './pages/pharmacy/MedicineMaster.jsx';
import RackLocations from './pages/pharmacy/RackLocations.jsx';
import PharmacyBills from './pages/pharmacy/PharmacyBills.jsx';
import StockAlerts from './pages/pharmacy/StockAlerts.jsx';
import Suppliers from './pages/pharmacy/Suppliers.jsx';
import PurchaseOrders from './pages/pharmacy/PurchaseOrders.jsx';
import GoodsReceipt from './pages/pharmacy/GoodsReceipt.jsx';
import PurchaseReturns from './pages/pharmacy/PurchaseReturns.jsx';
import LabTestMaster from './pages/lab/LabTestMaster.jsx';
import LabWorklist from './pages/lab/LabWorklist.jsx';
import LabCollection from './pages/lab/LabCollection.jsx';
import LabResultEntry from './pages/lab/LabResultEntry.jsx';
import LabReports from './pages/lab/LabReports.jsx';
import ImagingProcedures from './pages/radiology/ImagingProcedures.jsx';
import ImagingEquipment from './pages/radiology/ImagingEquipment.jsx';
import ImagingWorklist from './pages/radiology/ImagingWorklist.jsx';
import ImagingReporting from './pages/radiology/ImagingReporting.jsx';
import PndtFormF from './pages/radiology/PndtFormF.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import useTenantFeatures from './hooks/useTenantFeatures';

// The in-router error boundary (P26). One screen that throws while rendering
// used to unmount the whole tree and leave a blank page; this catches it, shows
// a card with a way back, and clears itself when the user navigates — keyed on
// the route, so the next screen is never hidden behind the last one's error.
// main.jsx holds an outer boundary for anything above the router.
export function RoutedErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

// The patient's doctor chat, gated on the HOSPITAL's `doctor_patient_chat`
// switch (P33, route half). FeatureGuard cannot do this: it gates a DOCTOR's
// plan and passes every patient straight through, so with the hospital's chat
// switched off a patient could still open the page and send into a
// conversation the doctor has no screen for. TenantFeatureRoute has the right
// check but staff wording ("contact your platform administrator"), which means
// nothing to a patient — so the locked state here says what a patient needs to
// know. Fails open like every tenant-feature gate; the server enforces the rule.
export function PatientChatFeatureRoute({ children }) {
  const { has } = useTenantFeatures();
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (has('doctor_patient_chat')) return children;

  return (
    <div className="w-full flex items-center justify-center py-16 px-4" data-testid="tenant-feature-locked-doctor_patient_chat">
      <div className="bg-white rounded-2xl shadow-md max-w-md w-full p-8 text-center">
        <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-slate-100 text-slate-500 flex items-center justify-center">
          <MessageCircleOff className="w-7 h-7" />
        </div>
        <h2 className="text-xl font-bold text-[#1a365d] mb-2">
          {t('patientChatLocked.title', 'Chat is not offered here')}
        </h2>
        <p className="text-sm text-slate-500 mb-6">
          {t('patientChatLocked.message', 'Your hospital does not offer messaging with your doctor. To reach your care team, contact the hospital directly or book an appointment.')}
        </p>
        <button
          type="button"
          onClick={() => navigate('/')}
          data-testid="tenant-feature-locked-cta-doctor_patient_chat"
          className="w-full px-4 py-3 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-lg cursor-pointer"
        >
          {t('patientChatLocked.back', 'Back to dashboard')}
        </button>
      </div>
    </div>
  );
}

// /patient-register on the provider app: a hand-off, never a form (owner,
// 1 Oct 2026 — patients register on the patient site and app only).
function PatientRegisterHandOff() {
  const { search } = useLocation();
  useEffect(() => {
    if (PATIENT_WEB_URL) window.location.replace(patientRegisterUrl(search));
  }, [search]);
  return PATIENT_WEB_URL ? null : <Navigate to="/" replace />;
}

// /doctor-register is no longer a platform flag ("Concept 2", owner, 1 Oct
// 2026): the page is allowed when it can offer a tenant. An invitation link
// (?invitationCode=) always can; otherwise the host tenant must allow
// voluntary doctor registration, or at least one tenant must
// (hooks/useDoctorRegistrationOpen). Waits for the lookup before bouncing.
export function DoctorRegistrationRouteGuard({ children }) {
  const location = useLocation();
  const invited = Boolean(new URLSearchParams(location.search).get('invitationCode'));
  const { loading, open } = useDoctorRegistrationOpen();
  if (invited) return children;
  if (!loading && !open) return <Navigate to="/" replace />;
  return children;
}

function AppContent() {
  const { errorType, setNetworkError, setServerError, clearError } = useError();
  const dispatch = useDispatch();

  // Set up global error handler for axios
  useEffect(() => {
    setGlobalErrorHandler({ setNetworkError, setServerError, clearError });
  }, [setNetworkError, setServerError, clearError]);

  // Master-data translation bootstrap.
  // 1. Fetch the language master once so the header language popup has the dynamic list.
  // 2. Hydrate the active locale synchronously from localStorage so first render uses
  //    cached labels (stale-while-revalidate — instant render, no flash).
  // 3. Fire a background fetch to refresh the cache.
  // 4. On every i18n language change, hydrate + refetch for the new language.
  // 5. On login (fires a `login` window event), re-run the fetch — logout calls
  //    localStorage.clear() which wipes the master-locale cache, so a fresh fetch
  //    is required once the user is authenticated again.
  useEffect(() => {
    const currentLcode = () =>
      i18n.language || localStorage.getItem('i18nextLng') || 'en';

    const loadLocale = (lcode) => {
      if (!lcode) return;
      dispatch(hydrateMasterLocaleFromCache(lcode));
      dispatch(fetchMasterLocale({ lcode }));
    };

    const refresh = () => {
      dispatch(getLanguages({ page: 1, limit: 100, search: '' }));
      loadLocale(currentLcode());
    };

    // Initial load on app mount.
    refresh();
    // Public pages (Home, Login, RegisterChoice) gate sign-up CTAs on the
    // auth.*Registration flags, so flags must load even before login. The
    // /feature/by-keys route is unauthenticated and the post-login layout
    // skips re-fetching when state.feature.loaded is already true.
    dispatch(fetchFeatureFlags());

    const handleLangChange = (lng) => loadLocale(lng);
    const handleLogin = () => refresh();
    const handleLogout = () => {
      // Re-seed with the current language so public pages (/login, /home) still
      // render translated labels after the logout wipe.
      loadLocale(currentLcode());
    };

    i18n.on('languageChanged', handleLangChange);
    window.addEventListener('login', handleLogin);
    window.addEventListener('logout', handleLogout);
    return () => {
      i18n.off('languageChanged', handleLangChange);
      window.removeEventListener('login', handleLogin);
      window.removeEventListener('logout', handleLogout);
    };
  }, [dispatch]);

  // Cross-tab session guard. Catches the case where another tab in the same
  // browser logs in or out as a different user — if it does, this tab's
  // sessionStorage id no longer matches localStorage's activeSessionId and
  // we exile this tab to /login. forceLogout is idempotent, so we don't need
  // a latch — repeated triggers from focus/storage events while we're already
  // on /login are no-ops.
  useEffect(() => {
    const checkSession = () => {
      if (isSessionMismatch()) forceLogout();
    };

    const onStorage = (e) => {
      // Only react to keys that signal an auth change.
      if (
        e.key === 'activeSessionId' ||
        e.key === 'accessToken' ||
        e.key === 'ID' ||
        e.key === null /* localStorage.clear() — key is null */
      ) {
        checkSession();
      }
    };
    const onFocus = () => checkSession();

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  // Get role from localStorage
  const userRole = localStorage.getItem('Role');
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

  // Decide which dashboard to show as index
  let DefaultDashboard;
  if (isAuthenticated && userRole) {
    if (userRole === 'doctor') {
      DefaultDashboard = <DoctorDashboard />;
    } else if (userRole === 'patient') {
      DefaultDashboard = <PatientDashboard />;
    } else if (userRole === 'user') {
      // Tenant staff. Their module screens (pharmacy counter, lab bench, scan
      // room) arrive with those modules; until then this is where they land.
      DefaultDashboard = <StaffDashboard />;
    } else if (userRole === 'admin') {
      // Super Admin no longer lives in this app — the dedicated admin portal
      // (Frontend-superadmin-web) took over. An old super-admin session is
      // sent to the notice page, which also signs it out.
      DefaultDashboard = localStorage.getItem('isSuperAdmin') === 'true'
        ? <Navigate to="/superadmin-moved" replace />
        : <AdminDashboard />;
    } else {
      DefaultDashboard = <Home />;
    }
  } else {
    DefaultDashboard = <Home />;
  }

  // Show error pages when network or server errors occur
  if (errorType === ERROR_TYPES.NETWORK) {
    return <NetworkErrorPage />;
  }

  if (errorType === ERROR_TYPES.SERVER) {
    return <ServerErrorPage />;
  }

  return (
    <Router data-testid="app-router">
      <RoutedErrorBoundary>
      <Routes data-testid="app-routes">
        {/* Public Routes - Always Accessible */}
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />
        {/* Static public pages — no session, no feature flag, always reachable
            (the footer links to them from every public page). */}
        <Route path="/about" element={<About />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register-choice" element={<RegisterChoice />} />
        <Route
          path="/doctor-register"
          element={
            <DoctorRegistrationRouteGuard>
              <DoctorRegister />
            </DoctorRegistrationRouteGuard>
          }
        />
        {/* NO PATIENT SIGN-UP ON THE PROVIDER APP (owner, 1 Oct 2026):
            patients register on the patient site and the patient app only.
            Old links and bookmarks to /patient-register are handed to the
            patient site's sign-up (or home, when this build knows none). */}
        <Route path="/patient-register" element={<PatientRegisterHandOff />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/activate" element={<ActivateAccount />} />
        {/* Super Admin moved to the dedicated admin portal — a super admin
            signing in (or holding an old session) lands here and is signed
            out. Public: it must render after the sign-out completes. */}
        <Route path="/superadmin-moved" element={<SuperAdminMoved />} />
        {/* Protected Routes - Require Authentication */}
        <Route element={<PrivateRoute />}>
          <Route element={<AppLayout />}>
            {/* Default Dashboard Route */}
            <Route path="/" element={DefaultDashboard} />

            {/* ---- Shared (all roles) ----
                doctorProfile: admin manages a doctor / doctor's own profile / patient views their doctor.
                patientProfile: admin + doctor view a patient's record / patient's own profile. */}
            <Route path="doctorProfile" element={<DoctorProfile />} />
            <Route path="patientProfile" element={<PatientProfile />} />
            <Route path="support" element={<Support />} />
            <Route path="supportchat" element={<SupportChatPage />} />
            {/* <Route path="chat" element={<ChatPage />} /> */}
            {/* <Route path="patientSummaryAI" element={<PatientSummaryAI />} /> */}

            {/* ---- Admin + Doctor ---- (admin uses the doctor dashboard as "view as a doctor") */}
            <Route element={<RoleRoute allow={['admin', 'doctor', 'super_admin']} />}>
              {/* 1 Oct 2026: the DOCTOR's plan must carry ai_reports (the
                  server refuses it otherwise); FeatureGuard passes the admin
                  and the super admin straight through. */}
              <Route element={<FeatureGuard control="ai_reports" />}>
                <Route path="aiReports" element={<AIReports />} />
              </Route>
            </Route>
            <Route element={<RoleRoute allow={['admin', 'doctor']} />}>
              <Route path="doctorDashboard" element={<DoctorDashboard />} />
            </Route>

            {/* The two appointment screens are the front desk's, not only the
                doctor's: the sidebar already offers them to a staff login that
                holds appointments.view, and the API now enforces the same
                permission on the appointment writes. Before this, staff clicked
                a menu item their own rail had shown them and were bounced to /
                with no message — nothing to report and nothing to explain.
                PermissionGuard is a no-op for admin and doctor. */}
            <Route element={<RoleRoute allow={['admin', 'doctor', 'user']} />}>
              <Route path="appointment" element={<PermissionGuard perm="appointments.view"><Appointment /></PermissionGuard>} />
              <Route path="appointmentList" element={<PermissionGuard perm="appointments.view"><AppointmentList /></PermissionGuard>} />
            </Route>

            {/* ---- Doctor + Patient (+ the hospital's admin) ----
                ADMIN ADDED (owner, 16 Sep 2026): a tenant admin may open ONE
                doctor's ledger, named by `?doctorId=` — the pages' existing
                admin branches (the Check / Undo mark-collected buttons) become
                live that way. With no doctorId the page keeps its own "pick a
                doctor" empty state; there is deliberately no hospital-wide
                ledger here.
                NO PLAN GATE (owner, 30 Sep 2026): the route used to sit under
                FeatureGuard control="online_payment", so a Free-plan doctor who
                takes cash had no way to see what was collected. Every doctor
                now reaches the ledger; the page itself hides its ONLINE parts
                when the plan lacks online_payment or the hospital has switched
                it off. An expired subscription is still locked by AppLayout's
                SubscriptionLockModal, not here. */}
            <Route element={<RoleRoute allow={['doctor', 'patient', 'admin']} />}>
              {/* Paying is a change, and it spends real money. */}
              <Route element={<ReadOnlyGuard />}>
                <Route path="payment" element={<Payment />} />
              </Route>
            </Route>

            {/* ---- Doctor + the hospital's admin ----
                The admin reads ONE doctor's plan, read-only, via `?doctorId=`
                (MySubscription is read-only for an admin). */}
            <Route element={<RoleRoute allow={['doctor', 'admin']} />}>
              <Route path="mySubscription" element={<MySubscription />} />
            </Route>

            {/* ---- Doctor only ---- */}
            <Route element={<RoleRoute allow={['doctor']} />}>
              <Route path="myPatient" element={<MyPatient />} />
              {/* SHARED WITH ME (Phase 4) — past visits patients have shared
                  with this doctor from the hospitals that treated them. Doctor
                  only, and deliberately not feature-gated: the share is the
                  patient's consent, and a hospital cannot switch off what its
                  doctor was personally trusted with.
                  PLAN GATE (owner, 1 Oct 2026): the doctor's plan must carry
                  visit_sharing — the server refuses the share endpoints
                  without it, so the page would only ever show a refusal. */}
              <Route element={<FeatureGuard control="visit_sharing" />}>
                <Route path="shared-visits" element={<SharedVisits />} />
              </Route>
              {/* Patient Library removed (India-SaaS decisions log #18): the
                  tenant-scoped Patients page is the library. */}
              {/* BOTH LAYERS, HOSPITAL FIRST (P33, doctor half). The route
                  carried the doctor's PLAN gate (FeatureGuard) only, so a
                  hospital that never bought chat still showed its doctors the
                  page — and every send came back 403 from requireTenantFeature.
                  TenantFeatureRoute is the hospital's switch, outermost for the
                  same reason as pharmacy / lab / radiology below: what the
                  hospital bought is asked before what the doctor's plan adds.
                  The patient side has said this since P33
                  (PatientChatFeatureRoute, in its own wording). */}
              <Route element={<TenantFeatureRoute feature="doctor_patient_chat" />}>
                <Route element={<FeatureGuard control="doctor_patient_chat" />}>
                  <Route path="patientchat" element={<PatientChatPage />} />
                </Route>
              </Route>
              {/* Doctor's medicines / products master list. The platform
                  Medicines flag was retired (1 Oct 2026) — always on. */}
              <Route path="medicines" element={<Medicines />} />
              {/* Doctor's personal settings (booking rules etc.) */}
              <Route path="doctorSettings" element={<DoctorSettingsPage />} />
            </Route>

            {/* ---- PHARMACY ----
                Open to tenant admins AND to staff whose sub-role grants it, so it
                sits outside the admin-only block below. Two gates apply: the
                hospital must have bought the module (TenantFeatureRoute), and the
                individual must hold the permission (PermissionGuard, a no-op for
                admins). The API enforces both again — this is only so nobody
                reaches a screen that would 403 on everything it tries. */}
            <Route element={<RoleRoute allow={['admin', 'user']} />}>
              <Route element={<TenantFeatureRoute feature="pharmacy" />}>
                <Route path="pharmacy/counter" element={<PermissionGuard perm="pharmacy.dispense"><PharmacyCounter /></PermissionGuard>} />
                <Route path="pharmacy/medicines" element={<PermissionGuard perm="pharmacy.stock.manage"><MedicineMaster /></PermissionGuard>} />
                <Route path="pharmacy/locations" element={<PermissionGuard perm="pharmacy.location.manage"><RackLocations /></PermissionGuard>} />
                <Route path="pharmacy/bills" element={<PermissionGuard perm="pharmacy.bill.reprint"><PharmacyBills /></PermissionGuard>} />
                <Route path="pharmacy/stock" element={<PermissionGuard perm="pharmacy.stock.manage"><StockAlerts /></PermissionGuard>} />
                {/* PURCHASING — a second tenant feature on top of `pharmacy`, because a
                    clinic that only dispenses does not buy from distributors. */}
                <Route element={<TenantFeatureRoute feature="pharmacy_inventory" />}>
                  <Route path="pharmacy/suppliers" element={<PermissionGuard perm="pharmacy.purchase.manage"><Suppliers /></PermissionGuard>} />
                  <Route path="pharmacy/purchase-orders" element={<PermissionGuard perm="pharmacy.purchase.manage"><PurchaseOrders /></PermissionGuard>} />
                  <Route path="pharmacy/goods-receipt" element={<PermissionGuard perm="pharmacy.purchase.manage"><GoodsReceipt /></PermissionGuard>} />
                  <Route path="pharmacy/purchase-returns" element={<PermissionGuard perm="pharmacy.return"><PurchaseReturns /></PermissionGuard>} />
                </Route>
              </Route>
            </Route>

            {/* ---- LABORATORY ----
                Same two-gate shape as the pharmacy: the hospital must have the
                module, and the individual must hold the permission. Result entry
                takes the order id in the path because the bench works one order
                at a time and the URL is what a technician bookmarks. */}
            <Route element={<RoleRoute allow={['admin', 'user', 'doctor']} />}>
              <Route element={<TenantFeatureRoute feature="lab" />}>
                <Route path="lab/tests" element={<PermissionGuard perm="lab.master.manage"><LabTestMaster /></PermissionGuard>} />
                <Route path="lab/worklist" element={<PermissionGuard perm="lab.order.view"><LabWorklist /></PermissionGuard>} />
                <Route path="lab/collection" element={<PermissionGuard perm="lab.sample.collect"><LabCollection /></PermissionGuard>} />
                <Route path="lab/results/:orderId" element={<PermissionGuard perm="lab.result.enter"><LabResultEntry /></PermissionGuard>} />
                <Route path="lab/reports" element={<PermissionGuard perm="lab.report.release"><LabReports /></PermissionGuard>} />
              </Route>
            </Route>

            {/* ---- RADIOLOGY & IMAGING ----
                Same two gates as pharmacy and lab. The Form F route takes the
                study id because the statutory record belongs to one study, and
                a technician needs to be able to reach it straight from the
                console when Perform refuses without it. */}
            <Route element={<RoleRoute allow={['admin', 'user', 'doctor']} />}>
              <Route element={<TenantFeatureRoute feature="radiology" />}>
                <Route path="radiology/worklist" element={<PermissionGuard perm="radiology.order.view"><ImagingWorklist /></PermissionGuard>} />
                <Route path="radiology/reporting" element={<PermissionGuard perm="radiology.report.draft"><ImagingReporting /></PermissionGuard>} />
                <Route path="radiology/form-f/:studyId" element={<PermissionGuard perm="radiology.study.perform"><PndtFormF /></PermissionGuard>} />
                <Route path="radiology/procedures" element={<PermissionGuard perm="radiology.master.manage"><ImagingProcedures /></PermissionGuard>} />
                <Route path="radiology/equipment" element={<PermissionGuard perm="radiology.master.manage"><ImagingEquipment /></PermissionGuard>} />
              </Route>
            </Route>

            {/* ---- TEAM CHAT (Phase 4, #14) ----
                Staff <-> staff on the chat-threads backbone. Two gates: the
                hospital's team_chat feature, and the sub-role's chat.team.view
                (a no-op for admins and doctors — they are not staff). */}
            <Route element={<RoleRoute allow={['admin', 'user', 'doctor']} />}>
              <Route element={<TenantFeatureRoute feature="team_chat" />}>
                <Route path="team-chat" element={<PermissionGuard perm="chat.team.view"><TeamChatPage /></PermissionGuard>} />
              </Route>
            </Route>

            {/* ---- UNIFIED BILLING (Phase 4, #16) ----
                The billing counter (lab/scan orders -> tax invoices) and the
                one-place-per-patient bill view. No TenantFeatureRoute: counter
                billing is deliberately feature-ungated (it rides the services
                the hospital already bought); the sub-role permission decides. */}
            <Route element={<RoleRoute allow={['admin', 'user', 'doctor']} />}>
              <Route path="billing/services" element={<PermissionGuard perm="billing.invoice.create"><ServiceBilling /></PermissionGuard>} />
              <Route path="billing/patient/:patientId" element={<PermissionGuard perm="billing.invoice.view"><PatientBilling /></PermissionGuard>} />
            </Route>

            {/* ---- HOME VISITS (Phase 5, #6) ----
                The admin-dispatched Care Taker board. Doctors read; the
                dispatch/perform split is the sub-role's. */}
            <Route element={<RoleRoute allow={['admin', 'user', 'doctor']} />}>
              <Route element={<TenantFeatureRoute feature="care_visits" />}>
                <Route path="care/visits" element={<PermissionGuard perm="care.visit.view"><CareVisits /></PermissionGuard>} />
              </Route>
            </Route>

            {/* ---- PER-SERVICE REPORTS (Phase 4, #16) ----
                The registry-driven analytics screen. Feature-gated per tab
                server-side; staff need reports.view (admins pass). */}
            {/* HOSPITAL CHECK SETS (check sets, Round A) — the hospital's own
                sets, which every doctor of the hospital may use or copy. The
                admin, or staff whose role has checks.manage (PermissionGuard
                passes the admin through); the hospital must have check_sets.
                The server enforces all three. */}
            <Route element={<RoleRoute allow={['admin', 'user']} />}>
              <Route element={<TenantFeatureRoute feature="check_sets" />}>
                <Route path="check-sets" element={<PermissionGuard perm="checks.manage"><HospitalCheckSets /></PermissionGuard>} />
              </Route>
            </Route>

            {/* HOSPITAL PROGRAMMES (care programmes, Round B) — the hospital's
                own follow-up programmes, which every doctor may start or copy.
                Same gate as the check sets page (checks.manage — no new key);
                the hospital must have care_programmes (which needs
                check_sets). The server enforces all of it. */}
            <Route element={<RoleRoute allow={['admin', 'user']} />}>
              <Route element={<TenantFeatureRoute feature="care_programmes" />}>
                <Route path="programmes" element={<PermissionGuard perm="checks.manage"><HospitalProgrammes /></PermissionGuard>} />
              </Route>
            </Route>

            <Route element={<RoleRoute allow={['admin', 'user']} />}>
              <Route element={<TenantFeatureRoute feature="reports" />}>
                <Route path="reports/services" element={<PermissionGuard perm="reports.view"><ServiceReports /></PermissionGuard>} />
              </Route>
            </Route>

            {/* ---- Staff (tenant employees) only ---- */}
            <Route element={<RoleRoute allow={['user']} />}>
              <Route path="staffDashboard" element={<StaffDashboard />} />
              {/* Their own profile + "How you sign in" (owner, 1 Oct 2026). */}
              <Route path="staffProfile" element={<StaffProfile />} />
            </Route>

            {/* ---- Patient only ---- */}
            <Route element={<RoleRoute allow={['patient']} />}>
              <Route path="patientDashboard" element={<PatientDashboard />} />
              <Route path="myAppointment" element={<MyAppointment />} />
              <Route path="myDoctor" element={<MyDoctor />} />
              {/* Gated on the hospital's doctor_patient_chat switch (P33). */}
              <Route path="doctorchat" element={<PatientChatFeatureRoute><DoctorChatPage /></PatientChatFeatureRoute>} />

              {/* PAGES THAT CHANGE SOMETHING.
                  While acting as a profile shared at READ level these render
                  blurred behind an explanation rather than disappearing —
                  hiding them leaves someone hunting for a menu item that used
                  to be there. The server refuses the writes regardless; this is
                  so nobody discovers that after filling in a form. */}
              <Route element={<ReadOnlyGuard />}>
                <Route path="patientAppointment" element={<PatientAppointment />} />
              </Route>
            </Route>

            {/* ---- Admin only ---- */}
            <Route element={<RoleRoute allow={['admin']} />}>
              <Route path="adminDashboard" element={<AdminDashboard />} />
              <Route path="adminProfile" element={<AdminProfile />} />

              {/* Doctors / Patients management */}
              <Route path="doctor" element={<Doctor />} />
              {/* Clinical patient records only matter where a service treats
                  them — currently medi_consult. Matches the sidebar's
                  nav-patient gate (Sidebar.jsx). */}
              <Route element={<TenantFeatureRoute feature="appointments" />}>
                <Route path="patient" element={<Patient />} />
              </Route>

              {/* OPEN-PLATFORM SCREENS — the Mitra One Care tenant's own, gated
                  on isOpenPlatform rather than on any purchased feature.
                  OpenPlatformRoute fails CLOSED; requireOpenPlatformAdmin
                  re-checks the flag against the DB on every request.

                  Doctor review is the reviewer's half of a backend that shipped
                  without one: an open-platform doctor stays invisible to every
                  patient until someone approves them here. (MOC Patients used to
                  live alongside it and has moved to the Super Admin portal.) */}
              <Route element={<OpenPlatformRoute />}>
                <Route path="doctor-review" element={<DoctorReview />} />
              </Route>

              {/* Users — the tenant's own EMPLOYEES and the sub-roles they hold.
                  Deliberately separate from Doctors and Patients: a doctor has a
                  licence and a plan, a patient is a global identity, and neither
                  is an employment record. Gated on the user_management tenant
                  feature, which the API enforces independently. */}
              <Route element={<TenantFeatureRoute feature="user_management" />}>
                <Route path="users" element={<Users />} />
                <Route path="user-roles" element={<UserRoles />} />
              </Route>

              {/* Reports Section — all four are medi_consult data (doctor /
                  patient / appointment counts), so they follow 'appointments'
                  the same as the Doctor group itself. */}
              <Route element={<TenantFeatureRoute feature="appointments" />}>
                <Route path="doctors" element={<ReportsDoctors />} />
                <Route path="patients" element={<ReportsPatients />} />
                {/* Doctor Count / Patient Count — one page, one series each
                    (`only`). `key` forces a remount when hopping between the
                    two, otherwise React reuses the instance and the filters /
                    data of the other variant linger. The bare /count is the
                    old combined page's URL — bookmarks land on Patient Count. */}
                <Route path="count" element={<Navigate replace to="/count/patients" />} />
                <Route path="count/doctors" element={<ReportsCount key="doctors" only="doctors" />} />
                <Route path="count/patients" element={<ReportsCount key="patients" only="patients" />} />
                <Route path="doctor-status" element={<ReportsDoctorStatus />} />
              </Route>

              {/* Subscription */}
              <Route path="subscription" element={<Subscription />} />
              <Route path="subscriptionPayments" element={<SubscriptionPayments />} />
              {/* /contactRequests REMOVED (22 Sep 2026). GET /contact-inquiry
                  and PUT /:id/responded are super_admin-only now — the list
                  spans every tenant, so a tenant admin reading it was a
                  cross-tenant leak. The screen lives in the Super Admin
                  console (Frontend-superadmin-web → Contact inquiries); here
                  it could only ever render a 403. */}
              {/* Sub-Items — the access-control catalogue a tenant's packages
                  draw from. Tenant-admin scope, same as the rest of billing. */}
              <Route path="sub-items" element={<SubItems />} />
              {/* Invite Section */}
              <Route path="clicktogo" element={<ClickToGo />} />
              {/* Admin Settings (feature flags) */}
              <Route path="settings" element={<Settings />} />

              {/* ---- Super-admin routes moved ----
                   Tenants, Admins, Platform Support, Master data, Translation,
                   Audit & Security now live in the dedicated admin portal
                   (Frontend-superadmin-web). A super admin here lands on
                   /superadmin-moved instead. */}
            </Route>
          </Route>
        </Route>

        {/* 404 Not Found */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </RoutedErrorBoundary>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Router>
  );
}

function App() {
  return (
    <ErrorProvider>
      <SupportSocketProvider>
        <AppContent />
      </SupportSocketProvider>
    </ErrorProvider>
  );
}

export default App;
